/**
 * Format presets for Report Builder columns.
 * Defines currency, number, percentage, date, and text formatting.
 */

export const FORMAT_TYPES = {
  text: { label: 'Text', type: 'text' },
  currency: { label: 'Currency ($)', type: 'currency', symbol: '$', decimals: 2 },
  number: { label: 'Number', type: 'number', decimals: 2 },
  integer: { label: 'Integer', type: 'number', decimals: 0 },
  percentage: { label: 'Percentage (%)', type: 'percentage', decimals: 2 },
  date: { label: 'Date (MM/DD/YYYY)', type: 'date', pattern: 'MM/DD/YYYY' },
  dateISO: { label: 'Date (YYYY-MM-DD)', type: 'date', pattern: 'YYYY-MM-DD' },
};

export const FORMAT_OPTIONS = Object.entries(FORMAT_TYPES).map(([key, val]) => ({
  value: key,
  label: val.label,
}));

export const ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

/**
 * Format a value based on format config.
 * @param {*} value - Raw value
 * @param {object} format - { type, symbol, decimals, pattern }
 * @returns {string} Formatted display string
 */
export function formatValue(value, format) {
  if (value === null || value === undefined || value === '') return '';
  if (!format || format.type === 'text') return String(value);

  const num = typeof value === 'number' ? value : parseFloat(value);

  switch (format.type) {
    case 'currency': {
      if (isNaN(num)) return String(value);
      const symbol = format.symbol || '$';
      const decimals = format.decimals ?? 2;
      return `${symbol}${num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
    }
    case 'number': {
      if (isNaN(num)) return String(value);
      const decimals = format.decimals ?? 2;
      return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }
    case 'percentage': {
      if (isNaN(num)) return String(value);
      const decimals = format.decimals ?? 2;
      return `${num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
    }
    case 'date': {
      try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        if (format.pattern === 'YYYY-MM-DD') {
          return d.toISOString().split('T')[0];
        }
        return d.toLocaleDateString('en-US');
      } catch {
        return String(value);
      }
    }
    default:
      return String(value);
  }
}

/**
 * Map format type to ExcelJS numFmt string.
 */
export function toExcelFormat(format) {
  if (!format) return undefined;
  switch (format.type) {
    case 'currency': {
      const dec = '0'.repeat(format.decimals ?? 2);
      return `${format.symbol || '$'}#,##0${dec ? '.' + dec : ''}`;
    }
    case 'number': {
      const dec = '0'.repeat(format.decimals ?? 2);
      return `#,##0${dec ? '.' + dec : ''}`;
    }
    case 'percentage': {
      const dec = '0'.repeat(format.decimals ?? 2);
      return `0${dec ? '.' + dec : ''}%`;
    }
    case 'date':
      return format.pattern === 'YYYY-MM-DD' ? 'yyyy-mm-dd' : 'mm/dd/yyyy';
    default:
      return undefined;
  }
}

/**
 * Detect the best default format for a column based on its key name.
 */
export function detectDefaultFormat(columnKey) {
  const moneyKeys = ['total_pay', 'hourly_rate', 'paid_amount', 'subtotal', 'tax_amount', 'total_amount', 'gross_pay', 'regular_pay', 'overtime_pay'];
  const numberKeys = ['hours_worked', 'break_minutes', 'entry_count'];
  const dateKeys = ['entry_date', 'start_date', 'end_date', 'invoice_date', 'due_date', 'paid_at'];

  if (moneyKeys.includes(columnKey)) return { type: 'currency', symbol: '$', decimals: 2 };
  if (numberKeys.includes(columnKey)) return { type: 'number', decimals: 2 };
  if (dateKeys.includes(columnKey)) return { type: 'date', pattern: 'MM/DD/YYYY' };
  return { type: 'text' };
}
